import React, { useState, useEffect, useCallback, useRef } from "react";
import config from "./config";

const FILE_ICONS = {
  pdf: "📕", doc: "📘", docx: "📘", txt: "📄", md: "📝",
  csv: "📊", xls: "📗", xlsx: "📗", json: "🔧", xml: "🔧",
  html: "🌐", htm: "🌐", ppt: "📙", pptx: "📙", rtf: "📄",
};

function getFileIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  return FILE_ICONS[ext] || "📎";
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const STATUS_CFG = {
  STARTING:    { icon: "🔄", label: "Starting",     color: "#eab308", spinning: true },
  IN_PROGRESS: { icon: "⏳", label: "Syncing",      color: "#3b82f6", spinning: true },
  COMPLETE:    { icon: "✅", label: "Synced",        color: "#22c55e", spinning: false },
  FAILED:      { icon: "❌", label: "Failed",        color: "#ef4444", spinning: false },
  STOPPED:     { icon: "⏹️", label: "Stopped",       color: "#71717a", spinning: false },
  NO_JOBS:     { icon: "⚪", label: "Never synced",  color: "#71717a", spinning: false },
  error:       { icon: "⚠️", label: "Error",         color: "#ef4444", spinning: false },
};

function shortKbName(name) {
  return (name || "")
    .replace("saenablement-rag-", "")
    .replace("default-", "Default · ")
    .replace("semantic-", "Semantic · ")
    .replace("titan", "Titan V2")
    .replace("cohere", "Cohere ML");
}

export default function DocumentsPanel({ token, dark }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncJobs, setSyncJobs] = useState(null); // { kb-key: { status, statistics, ... } }
  const [syncJobIds, setSyncJobIds] = useState(null); // { kb-key: jobId }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { key, name }
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);
  const t = dark ? darkT : lightT;

  // Compute per-file sync status across all KBs
  function getFileSyncStatus(fileName, jobs) {
    if (!jobs) return null; // no sync data yet
    const kbKeys = Object.keys(jobs);
    const doneKBs = kbKeys.filter(k => jobs[k].status === "COMPLETE" || jobs[k].status === "FAILED");
    if (doneKBs.length === 0) return null; // still syncing

    let failedIn = 0;
    let succeededIn = 0;
    let knownToAnyKB = false;

    for (const k of doneKBs) {
      const failedFiles = jobs[k].failedFiles || [];
      const scanned = jobs[k].statistics?.scanned || 0;

      if (failedFiles.includes(fileName)) {
        failedIn++;
        knownToAnyKB = true;
      } else if (scanned > 0) {
        // File wasn't in the failed list AND the KB did scan docs.
        // But we can't be 100% sure this file was part of that sync
        // unless the scanned count matches or exceeds the doc count.
        // Use the sync timestamp: if file was uploaded AFTER the sync, it wasn't included.
        const doc = documents.find(d => d.name === fileName);
        const syncTime = jobs[k].updatedAt;
        if (doc && syncTime && new Date(doc.lastModified) < new Date(syncTime)) {
          succeededIn++;
          knownToAnyKB = true;
        }
      }
    }

    if (!knownToAnyKB) return null; // file wasn't part of any sync
    if (failedIn === 0 && succeededIn > 0) return "synced";
    if (succeededIn === 0 && failedIn > 0) return "failed";
    if (succeededIn > 0 && failedIn > 0) return "partial";
    return null;
  }

  // ── Fetch documents ──
  const fetchDocuments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(config.apiBase + "/rag/documents", { headers: { Authorization: token } });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setDocuments(data.documents || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // ── Upload ──
  const uploadFile = async (file) => {
    const params = new URLSearchParams({ filename: file.name, contentType: file.type || "application/octet-stream" });
    const urlRes = await fetch(config.apiBase + `/rag/upload-url?${params}`, { headers: { Authorization: token } });
    const urlData = await urlRes.json();
    if (urlData.error) throw new Error(urlData.error);
    await fetch(urlData.uploadUrl, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
    return urlData.key;
  };

  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB Bedrock KB limit

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files);

    // Check for oversized files
    const oversized = fileList.filter(f => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      const names = oversized.map(f => `${f.name} (${formatSize(f.size)})`).join(", ");
      setError(`Files exceed 50 MB Bedrock KB limit: ${names}`);
      return;
    }
    setUploading(true);
    setError("");
    setUploadQueue(fileList.map(f => ({ name: f.name, status: "pending" })));
    for (let i = 0; i < fileList.length; i++) {
      setUploadQueue(prev => prev.map((item, idx) => idx === i ? { ...item, status: "uploading" } : item));
      try {
        await uploadFile(fileList[i]);
        setUploadQueue(prev => prev.map((item, idx) => idx === i ? { ...item, status: "done" } : item));
      } catch (e) {
        setUploadQueue(prev => prev.map((item, idx) => idx === i ? { ...item, status: "error", error: e.message } : item));
      }
    }
    setUploading(false);
    setTimeout(() => setUploadQueue([]), 2000);
    fetchDocuments();
  };

  const handleDelete = async (key, name) => {
    setDeleteConfirm({ key, name });
  };

  const confirmDeleteAction = async () => {
    if (!deleteConfirm) return;
    const { key } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await fetch(config.apiBase + `/rag/documents?key=${encodeURIComponent(key)}`, { method: "DELETE", headers: { Authorization: token } });
      setDocuments(prev => prev.filter(d => d.key !== key));
    } catch (e) { setError(e.message); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); };
  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };

  // ── Sync ──
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  const pollSyncStatus = useCallback(async (jobIds) => {
    try {
      const jobIdsParam = Object.entries(jobIds).map(([k, v]) => `${k}:${v}`).join(",");
      const res = await fetch(config.apiBase + `/rag/sync-status?jobIds=${encodeURIComponent(jobIdsParam)}`, { headers: { Authorization: token } });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSyncJobs(data.jobs);

      // Check if all done
      const allDone = Object.values(data.jobs).every(j =>
        j.status === "COMPLETE" || j.status === "FAILED" || j.status === "STOPPED"
      );
      if (allDone) {
        stopPolling();
        setSyncing(false);
      }
    } catch (e) {
      console.error("Sync poll error:", e);
    }
  }, [token, stopPolling]);

  const handleSync = async () => {
    setSyncing(true);
    setError("");
    setSyncJobs(null);
    setSyncJobIds(null);
    try {
      const res = await fetch(config.apiBase + "/rag/sync", {
        method: "POST", headers: { Authorization: token, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      // Extract job IDs
      const ids = {};
      const initialJobs = {};
      for (const [key, job] of Object.entries(data.jobs || {})) {
        if (job.ingestionJobId) ids[key] = job.ingestionJobId;
        initialJobs[key] = { ...job, status: job.status === "started" ? "STARTING" : job.status };
      }
      setSyncJobIds(ids);
      setSyncJobs(initialJobs);

      // Start polling every 4 seconds
      stopPolling();
      pollRef.current = setInterval(() => pollSyncStatus(ids), 4000);
      // Also poll immediately after a short delay
      setTimeout(() => pollSyncStatus(ids), 1500);
    } catch (e) {
      setError(e.message);
      setSyncing(false);
    }
  };

  // ── Fetch latest sync status on mount ──
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(config.apiBase + "/rag/sync-status", { headers: { Authorization: token } });
        const data = await res.json();
        if (data.jobs) setSyncJobs(data.jobs);
      } catch (_) {}
    })();
  }, [token]);

  return (
    <div style={{ marginBottom: "20px" }}>
      <div style={{
        fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "10px",
        textTransform: "uppercase", letterSpacing: "0.05em",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span>📁 RAG Documents</span>
        <span style={{ fontSize: "10px", fontWeight: 400, color: t.muted, textTransform: "none", letterSpacing: 0 }}>
          {documents.length} file{documents.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Drop zone */}
      <div onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        onClick={() => !uploading && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? t.accent : t.border}`, borderRadius: "10px",
          padding: "16px", textAlign: "center", cursor: uploading ? "default" : "pointer",
          backgroundColor: dragOver ? (dark ? "rgba(232,101,26,0.08)" : "rgba(9,114,211,0.05)") : t.dropBg,
          transition: "all 0.2s", marginBottom: "12px",
        }}>
        <input ref={fileInputRef} type="file" multiple style={{ display: "none" }}
          onChange={(e) => handleUpload(e.target.files)}
          accept=".pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx,.json,.xml,.html,.htm,.ppt,.pptx,.rtf" />
        <div style={{ fontSize: "24px", marginBottom: "4px" }}>{uploading ? "⏳" : dragOver ? "📥" : "☁️"}</div>
        <div style={{ fontSize: "12px", fontWeight: 600, color: t.text, marginBottom: "2px" }}>
          {uploading ? "Uploading..." : "Drop files here or click to browse"}
        </div>
        <div style={{ fontSize: "10px", color: t.muted }}>PDF, DOCX, TXT, MD, CSV, JSON, HTML, PPTX</div>
      </div>

      {/* Upload progress */}
      {uploadQueue.length > 0 && (
        <div style={{ marginBottom: "12px" }}>
          {uploadQueue.map((item, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 10px", borderRadius: "6px", marginBottom: "4px", backgroundColor: t.itemBg, fontSize: "11px" }}>
              <span>{item.status === "done" ? "✅" : item.status === "error" ? "❌" : item.status === "uploading" ? "⏳" : "⏸️"}</span>
              <span style={{ flex: 1, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</span>
              {item.status === "error" && <span style={{ color: "#ef4444", fontSize: "10px" }}>{item.error}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: "8px 10px", borderRadius: "6px", marginBottom: "10px",
          backgroundColor: dark ? "rgba(239,68,68,0.1)" : "#fef2f2",
          border: `1px solid ${dark ? "rgba(239,68,68,0.3)" : "#fecaca"}`,
          fontSize: "11px", color: dark ? "#fca5a5" : "#dc2626" }}>
          ⚠️ {error}
        </div>
      )}

      {/* Document list */}
      <div style={{ maxHeight: "200px", overflowY: "auto", borderRadius: "8px",
        border: documents.length > 0 ? `1px solid ${t.border}` : "none" }}>
        {loading && documents.length === 0 ? (
          <div style={{ padding: "16px", textAlign: "center", fontSize: "12px", color: t.muted }}>Loading documents...</div>
        ) : documents.length === 0 ? (
          <div style={{ padding: "16px", textAlign: "center", fontSize: "12px", color: t.muted }}>No documents yet. Upload files to get started.</div>
        ) : documents.map((doc, idx) => {
          // Compute per-file sync status from all KBs
          const fileStatus = getFileSyncStatus(doc.name, syncJobs);
          return (
          <div key={doc.key} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px",
            borderBottom: idx < documents.length - 1 ? `1px solid ${t.border}` : "none", backgroundColor: t.itemBg }}>
            <span style={{ fontSize: "16px", flexShrink: 0 }}>{getFileIcon(doc.name)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ fontSize: "12px", fontWeight: 600, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.name}>{doc.name}</span>
                {fileStatus === "synced" && <span title="Synced to all KBs" style={{ fontSize: "11px", flexShrink: 0 }}>✅</span>}
                {fileStatus === "partial" && <span title="Failed in some KBs (e.g. too large for semantic chunking)" style={{ fontSize: "11px", flexShrink: 0 }}>⚠️</span>}
                {fileStatus === "failed" && <span title="Failed in all KBs" style={{ fontSize: "11px", flexShrink: 0 }}>❌</span>}
              </div>
              <div style={{ fontSize: "10px", color: t.muted, display: "flex", gap: "4px", alignItems: "center" }}>
                <span>{formatSize(doc.size)} · {timeAgo(doc.lastModified)}</span>
                {fileStatus === "partial" && <span style={{ color: "#eab308" }}>· partial sync</span>}
                {fileStatus === "failed" && <span style={{ color: "#ef4444" }}>· sync failed</span>}
              </div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); handleDelete(doc.key, doc.name); }} title="Delete"
              style={{ background: "none", border: "none", cursor: "pointer", color: t.muted, fontSize: "14px", padding: "2px 4px", borderRadius: "4px", flexShrink: 0, opacity: 0.6 }}
              onMouseEnter={(e) => { e.target.style.opacity = "1"; e.target.style.color = "#ef4444"; }}
              onMouseLeave={(e) => { e.target.style.opacity = "0.6"; e.target.style.color = t.muted; }}>🗑</button>
          </div>
          );
        })}
      </div>

      {/* Refresh */}
      {documents.length > 0 && (
        <button onClick={fetchDocuments} disabled={loading}
          style={{ width: "100%", marginTop: "8px", padding: "6px", backgroundColor: "transparent",
            border: `1px solid ${t.border}`, borderRadius: "6px", color: t.muted, cursor: "pointer", fontSize: "11px" }}>
          {loading ? "Refreshing..." : "🔄 Refresh"}
        </button>
      )}

      {/* ── Sync to Knowledge Bases ── */}
      <div style={{ marginTop: "16px" }}>
        <div style={{ fontSize: "12px", fontWeight: 700, color: t.accent, marginBottom: "8px",
          textTransform: "uppercase", letterSpacing: "0.05em" }}>
          🔗 Sync to Knowledge Bases
        </div>
        <p style={{ fontSize: "10px", color: t.muted, marginBottom: "10px", marginTop: 0, lineHeight: 1.4 }}>
          Triggers ingestion across all 4 KB configurations (chunking × embedding combos)
        </p>

        <button onClick={handleSync} disabled={syncing || documents.length === 0}
          style={{
            width: "100%", padding: "10px", border: "none", borderRadius: "8px", cursor: syncing || documents.length === 0 ? "default" : "pointer",
            fontSize: "12px", fontWeight: 700, color: "#fff", transition: "opacity 0.2s",
            background: syncing
              ? (dark ? "rgba(59,130,246,0.6)" : "rgba(59,130,246,0.6)")
              : `linear-gradient(135deg, ${t.accent}, ${dark ? "#00d4ff" : "#0550a0"})`,
            opacity: documents.length === 0 ? 0.4 : 1,
          }}>
          {syncing ? "⏳ Syncing..." : "🚀 Sync All Knowledge Bases"}
        </button>

        {/* Sync status per KB */}
        {syncJobs && (
          <div style={{ marginTop: "10px" }}>
            {Object.entries(syncJobs).map(([key, job]) => {
              const cfg = STATUS_CFG[job.status] || STATUS_CFG.error;
              const stats = job.statistics || {};
              const totalProcessed = (stats.indexed || 0) + (stats.modified || 0) + (stats.deleted || 0) + (stats.failed || 0);
              const scanned = stats.scanned || 0;
              const progressPct = scanned > 0 ? Math.min(100, Math.round((totalProcessed / scanned) * 100)) : 0;
              const isActive = job.status === "IN_PROGRESS" || job.status === "STARTING";
              const isDone = job.status === "COMPLETE";
              const isFailed = job.status === "FAILED";

              return (
                <div key={key} style={{
                  padding: "8px 10px", borderRadius: "8px", marginBottom: "6px",
                  border: `1px solid ${isDone ? "rgba(34,197,94,0.2)" : isFailed ? "rgba(239,68,68,0.2)" : t.border}`,
                  backgroundColor: isDone ? (dark ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.04)") :
                    isFailed ? (dark ? "rgba(239,68,68,0.06)" : "rgba(239,68,68,0.04)") : t.itemBg,
                }}>
                  {/* Header row */}
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: isActive ? "6px" : "0" }}>
                    <span style={{ fontSize: "12px" }}>{cfg.icon}</span>
                    <span style={{ flex: 1, fontSize: "11px", fontWeight: 600, color: t.text }}>
                      {shortKbName(job.name || key)}
                    </span>
                    <span style={{ fontSize: "10px", fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
                  </div>

                  {/* Progress bar for active jobs */}
                  {isActive && (
                    <div style={{ marginBottom: "4px" }}>
                      <div style={{ height: "4px", borderRadius: "2px", backgroundColor: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", overflow: "hidden" }}>
                        <div style={{
                          height: "100%", borderRadius: "2px", transition: "width 0.5s ease",
                          width: scanned > 0 ? `${progressPct}%` : "30%",
                          background: `linear-gradient(90deg, ${cfg.color}, ${cfg.color}aa)`,
                          animation: scanned === 0 ? "pulse 1.5s ease-in-out infinite" : "none",
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Stats row */}
                  {scanned > 0 && (
                    <div style={{ display: "flex", gap: "8px", fontSize: "9px", color: t.muted, flexWrap: "wrap" }}>
                      <span>📄 {scanned} scanned</span>
                      {(stats.indexed || 0) > 0 && <span>✅ {stats.indexed} indexed</span>}
                      {(stats.modified || 0) > 0 && <span>📝 {stats.modified} updated</span>}
                      {(stats.deleted || 0) > 0 && <span>🗑 {stats.deleted} deleted</span>}
                      {(stats.failed || 0) > 0 && <span style={{ color: "#ef4444" }}>❌ {stats.failed} failed</span>}
                    </div>
                  )}

                  {/* Failed files */}
                  {(isFailed || (isDone && (stats.failed || 0) > 0)) && job.failedFiles && job.failedFiles.length > 0 && (
                    <div style={{ fontSize: "9px", color: "#ef4444", marginTop: "4px" }}>
                      Failed: {job.failedFiles.join(", ")}
                    </div>
                  )}

                  {/* Friendly failure reason */}
                  {(isFailed || (isDone && (stats.failed || 0) > 0)) && job.failureReasons && job.failureReasons.length > 0 && (
                    <div style={{ fontSize: "9px", color: t.muted, marginTop: "2px", fontStyle: "italic" }}>
                      {job.failureReasons[0].includes("semantic chunking")
                        ? "Extracted text exceeds 1 MB limit for semantic chunking"
                        : job.failureReasons[0].includes("MaximumFileSizeSupported")
                        ? "File exceeds 50 MB size limit"
                        : job.failureReasons[0].includes("Filterable metadata")
                        ? "Metadata size limit exceeded in S3 Vectors"
                        : job.failureReasons[0].substring(0, 100)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }} onClick={() => setDeleteConfirm(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            backgroundColor: dark ? "#1e1440" : "#fff",
            border: `1px solid ${t.border}`,
            borderRadius: "14px", padding: "24px", width: "320px",
            boxShadow: dark ? "0 12px 40px rgba(0,0,0,0.6)" : "0 12px 40px rgba(0,0,0,0.15)",
          }}>
            <div style={{ fontSize: "28px", textAlign: "center", marginBottom: "12px" }}>🗑️</div>
            <div style={{ fontSize: "15px", fontWeight: 700, color: t.text, textAlign: "center", marginBottom: "6px" }}>
              Delete Document
            </div>
            <div style={{ fontSize: "12px", color: t.muted, textAlign: "center", marginBottom: "20px", lineHeight: 1.5 }}>
              Are you sure you want to delete<br />
              <span style={{ fontWeight: 600, color: t.text }}>{deleteConfirm.name}</span>?
              <br />This cannot be undone.
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{
                  flex: 1, padding: "10px", borderRadius: "8px", fontSize: "13px", fontWeight: 600,
                  border: `1px solid ${t.border}`, backgroundColor: "transparent",
                  color: t.muted, cursor: "pointer",
                }}>
                Cancel
              </button>
              <button onClick={confirmDeleteAction}
                style={{
                  flex: 1, padding: "10px", borderRadius: "8px", fontSize: "13px", fontWeight: 600,
                  border: "none", backgroundColor: "#ef4444", color: "#fff", cursor: "pointer",
                }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const lightT = {
  accent: "#0972d3", text: "#18181b", muted: "#71717a",
  border: "#e4e4e7", dropBg: "#fafafa", itemBg: "#ffffff",
};
const darkT = {
  accent: "#e8651a", text: "#f0e6ff", muted: "#b8a0d8",
  border: "rgba(232, 101, 26, 0.2)", dropBg: "rgba(30, 20, 64, 0.4)", itemBg: "rgba(30, 20, 64, 0.3)",
};
